import { CustomTooltip, IconButton } from '@fountain/fountain-ui-components';
import { FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';
import cx from 'classnames';
import React, { FC } from 'react';

import { Icon, InformationCircleOutlineIcon } from '@fountain/ripple/icon';

import { useStyles } from './styles';

export interface SettingSwitchProps {
  checked: boolean;
  dataKey: string;
  // Helper text under the label, announced as the switch's description.
  description?: React.ReactNode;
  disabled?: boolean;
  fieldComponent?: React.ReactNode;
  label: React.ReactNode;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tooltipDescription?: string;
}

export const SettingSwitch: FC<SettingSwitchProps> = ({
  checked,
  dataKey,
  description,
  disabled = false,
  fieldComponent,
  label,
  onChange,
  tooltipDescription,
}) => {
  const styles = useStyles();
  const descriptionId = `${dataKey}-description`;

  return (
    <Grid item xs={12} className={styles.fieldRow}>
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            disabled={disabled}
            data-testid={dataKey}
            inputProps={
              description ? { 'aria-describedby': descriptionId } : undefined
            }
            onChange={onChange}
          />
        }
        label={
          <Grid container alignItems="center">
            <Typography
              variant="body2"
              className={cx(styles.switchLabel, {
                [styles.disabled]: disabled,
                [styles.enabled]: !disabled,
              })}
            >
              {label}
            </Typography>
            {tooltipDescription && (
              <CustomTooltip
                dense
                disableFocusListener
                enterTouchDelay={100}
                title={tooltipDescription}
                className={styles.tooltip}
              >
                <IconButton
                  disableRipple
                  size="small"
                  className={styles.infoButton}
                  aria-label={tooltipDescription}
                >
                  <Icon
                    icon={InformationCircleOutlineIcon}
                    size="sm"
                    color="inherit"
                  />
                </IconButton>
              </CustomTooltip>
            )}
          </Grid>
        }
      />
      {description && (
        <Typography
          id={descriptionId}
          variant="overline"
          color="textSecondary"
          display="block"
          className={styles.description}
        >
          {description}
        </Typography>
      )}
      {fieldComponent && (
        <Grid className={styles.fieldComponent}>{fieldComponent}</Grid>
      )}
    </Grid>
  );
};
